import { useState } from "react";
import type { Contract, ContractCost, EnergyFlow, ScenarioParams } from "../lib/types";
import { eur } from "../lib/format";
import { evaluateContractClient } from "../lib/calculator";
import { MonthlyFlowChart } from "../charts/MonthlyFlowChart";

interface Props {
  contracts: Contract[];
  costs: ContractCost[];
  flow: EnergyFlow;
  scenario: ScenarioParams;
}

export function MonthlyOverview({ contracts, costs, flow, scenario }: Props) {
  const [selectedId, setSelectedId] = useState<number>(contracts[0]?.id ?? 0);
  const selectedContract = contracts.find((c) => c.id === selectedId);
  const selectedCost = costs.find((c) => c.contract_id === selectedId);
  const showCurtailable = flow.year_totals.curtailable_kwh > 0;

  // Per maand kosten via client calculator (we hebben geen per-maand evaluatie endpoint)
  // Dat is acceptabel omdat per-maand evaluatie niet exact werkt voor saldering (jaarbasis).
  // We tonen alleen de KWH flows per maand + jaartotaal kosten van het contract.
  void selectedCost;

  return (
    <div className="card mt-4 p-0 overflow-hidden">
      <div className="px-5 pt-4 pb-3 flex justify-between items-center flex-wrap gap-2.5 border-b border-line">
        <div>
          <h3 className="serif m-0 text-lg font-medium">Per maand</h3>
          <span className="help">
            Energie‑flows per maand. Kosten zijn op jaarbasis i.v.m. saldering.
          </span>
        </div>
        {selectedContract && (
          <select
            className="input input-text w-auto min-w-[200px]"
            value={selectedId}
            onChange={(e) => setSelectedId(parseInt(e.target.value))}
          >
            {contracts.map((c) => {
              const cost = costs.find((co) => co.contract_id === c.id);
              return (
                <option key={c.id} value={c.id}>
                  {c.name} — {eur(cost?.total_eur ?? 0)}
                </option>
              );
            })}
          </select>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-xs mono">
          <thead>
            <tr className="bg-bg border-b border-line-strong text-right">
              <th className="text-left px-3 py-2.5 font-medium text-ink-muted">Maand</th>
              <th className="px-2 py-2.5 font-medium text-ink-muted">Productie</th>
              <th className="px-2 py-2.5 font-medium text-ink-muted">Verbruik</th>
              <th className="px-2 py-2.5 font-medium text-ink-muted">Import</th>
              <th className="px-2 py-2.5 font-medium text-ink-muted">Export</th>
              {showCurtailable && (
                <th className="px-2 py-2.5 font-medium text-ink-muted">
                  {scenario.curtailment_enabled ? "Curtail." : "Curtailbr"}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {flow.months.map((m) => (
              <tr key={m.name} className="border-b border-dashed border-line text-right">
                <td className="text-left px-3 py-2 capitalize font-medium">{m.name}</td>
                <td className="px-2 py-2">{m.production_kwh.toFixed(0)}</td>
                <td className="px-2 py-2">{m.consumption_kwh.toFixed(0)}</td>
                <td className="px-2 py-2 text-rust">{m.import_kwh.toFixed(0)}</td>
                <td className="px-2 py-2 text-green-light">{m.export_kwh.toFixed(0)}</td>
                {showCurtailable && (
                  <td className={`px-2 py-2 ${m.curtailable_kwh > 0 ? "text-terra" : "text-ink-subtle"}`}>
                    {m.curtailable_kwh > 0
                      ? (scenario.curtailment_enabled ? m.curtailed_kwh : m.curtailable_kwh).toFixed(0)
                      : "—"}
                  </td>
                )}
              </tr>
            ))}
            <tr className="bg-bg border-t border-line-strong text-right font-semibold">
              <td className="text-left px-3 py-2.5">Totaal</td>
              <td className="px-2 py-2.5">
                {flow.months.reduce((s, m) => s + m.production_kwh, 0).toFixed(0)}
              </td>
              <td className="px-2 py-2.5">
                {flow.months.reduce((s, m) => s + m.consumption_kwh, 0).toFixed(0)}
              </td>
              <td className="px-2 py-2.5 text-rust">{flow.year_totals.import_kwh.toFixed(0)}</td>
              <td className="px-2 py-2.5 text-green-light">
                {flow.year_totals.export_kwh.toFixed(0)}
              </td>
              {showCurtailable && (
                <td className="px-2 py-2.5 text-terra">
                  {(scenario.curtailment_enabled
                    ? flow.year_totals.curtailed_kwh
                    : flow.year_totals.curtailable_kwh
                  ).toFixed(0)}
                </td>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      <div className="px-5 py-4 border-t border-line">
        <MonthlyFlowChart flow={flow} />
      </div>
    </div>
  );

  // Stop unused warning
  void evaluateContractClient;
}
